/**
 * What an admin may do, as the owner hands it out.
 *
 * Roles set rank; grants give admins their powers. The owner can do
 * everything, an admin exactly what they have been given, and members hold
 * nothing. What this page offers follows from that, and is presentation only —
 * the api refuses whatever the grants do not allow, however the page renders.
 */

export type GrantName = "manage_members" | "manage_events" | "verify_scores" | "moderate";

export const GRANTS: { name: GrantName; label: string; description: string }[] = [
  {
    name: "manage_members",
    label: "Manage members",
    description: "invite members, approve or decline requests, remove members",
  },
  {
    name: "manage_events",
    label: "Manage events",
    description: "post, edit and delete events, and see drafts and scheduled ones",
  },
  {
    name: "verify_scores",
    label: "Verify scores",
    description: "verify results and withdraw a verification, and see the results waiting for one",
  },
  {
    name: "moderate",
    label: "Moderate",
    description: "hide other people's results and photos on this space's boards, and delete their comments",
  },
];

/**
 * A grant as a person reads it. The set grows by addition, so a name this
 * build has never heard of is shown as it is rather than dropped.
 */
export function grantLabel(name: string): string {
  return GRANTS.find((grant) => grant.name === name)?.label ?? name;
}

/** Whether a member may do what a grant allows: the owner always may. */
export function holds(role: string, grants: string[], grant: GrantName): boolean {
  return role === "owner" || grants.includes(grant);
}

/** Who is looking at the members list, as the page knows them. */
export type Viewer = {
  me: string;
  isOwner: boolean;
  /** The owner's, and an admin's holding manage_members; never in a personal space. */
  canManageMembers: boolean;
  isPersonal: boolean;
};

/** What a viewer may do to one member: each part of the member's sheet. */
export type Powers = {
  /** Member or admin: the owner's alone. */
  role: boolean;
  /** Which grants an admin holds: the owner's alone, and only on an admin. */
  grants: boolean;
  /** Make them the owner instead: the owner's alone. */
  handOn: boolean;
  /** Take them out of the space. */
  remove: boolean;
};

/**
 * What the viewer may do to `member`, as the api allows it. Nobody acts on
 * themselves here — leaving is the space's menu's — and nothing applies in a
 * personal space, which has one member for its whole life.
 *
 * Changing a role, giving grants and handing the space on are the owner's,
 * whatever an admin holds. Removing is managing members, which reaches
 * members only: no grant reaches another admin, and the page offers the
 * owner no way to remove one either — an admin is made a member first.
 */
export function powersOver(viewer: Viewer, member: { userId: string; role: string }): Powers {
  const somebodyElse = member.userId !== viewer.me && !viewer.isPersonal;
  const owner = viewer.isOwner && somebodyElse && member.role !== "owner";
  return {
    role: owner,
    grants: owner && member.role === "admin",
    handOn: owner,
    remove: viewer.canManageMembers && somebodyElse && member.role === "member",
  };
}

/** Whether a member's row opens a sheet at all: something in it is the viewer's to do. */
export function mayManage(powers: Powers): boolean {
  return powers.role || powers.grants || powers.handOn || powers.remove;
}
